import { useState, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  adminRequest,
  fetchAdminConsumoIa,
  fetchAdminConsumoHistorico,
  fetchAdminConfigIA,
  updateAdminConfigIA,
  fetchAdminProveedoresIA,
  updateAdminProveedorIa,
  fetchAdminProveedorApiKey,
  testAdminProveedorIa,
  fetchAdminRAGStatus,
  fetchAdminRAGHistory,
  fetchAdminDashboardIA,
  reindexRAG,
  type ReindexLogEntry,
  type AdminAssistMode,
  type AdminGlossaryTerm,
  type AdminProveedorRaw,
  type AdminProveedorModeloRaw,
} from '../../../shared/services/api';
import type {
  Proveedor,
  AssistMode,
  GlossaryTerm,
  ChartMonth,
  DashboardData,
  NewProviderForm,
} from '../components/admin-ia-shared';
import { DASHBOARD_FALLBACK, INITIAL_GLOSSARY } from '../components/admin-ia-shared';

export interface UseAdminIAReturn {
  // Navigation
  activePill: PillKey;
  setActivePill: Dispatch<SetStateAction<PillKey>>;

  // Global state
  loading: boolean;
  error: string | null;
  saving: boolean;
  feedbackMsg: string;
  loadData: () => Promise<void>;

  // Dashboard
  dashData: DashboardData;
  dashLoading: boolean;
  dashFiltering: boolean;
  dashPeriodo: string;
  handlePeriodoChange: (p: string) => void;

  // Providers
  providers: Proveedor[];
  showNewProvider: boolean;
  newProvider: NewProviderForm;
  setShowNewProvider: Dispatch<SetStateAction<boolean>>;
  setNewProvider: Dispatch<SetStateAction<NewProviderForm>>;
  updateProviderLocal: (id: string, patch: Partial<Proveedor>) => void;
  handleSaveProviderKey: (prov: Proveedor) => Promise<void>;
  handleEditKey: (provId: string) => Promise<void>;
  handleCancelEditKey: (provId: string) => void;
  handleToggleProviderActive: (prov: Proveedor) => Promise<void>;
  handleTestKey: (id: string) => Promise<void>;
  handleCreateProvider: () => Promise<void>;
  providerTypeLabel: (tipo: string) => string;

  // Chat config
  chatModel: string;
  chatLimit: number;
  chatProviderOptions: { value: string; label: string }[];
  assistModelOptions: { value: string; label: string }[];
  fallbackChain: string[];
  configData: Record<string, any> | null;
  ragStatus: { totalChunks: number; docsIndexados: number; ultimaIndexacion: string } | null;
  reindexing: boolean;
  reindexForce: boolean;
  setReindexForce: Dispatch<SetStateAction<boolean>>;
  reindexIntervalMs: number;
  handleReindexIntervalChange: (ms: number) => void;
  reindexHistory: ReindexLogEntry[];
  handleChatModelChange: (value: string) => void;
  handleChatLimitChange: (value: number) => void;
  removeFallback: (idx: number) => void;
  addFallback: (tipo: string) => void;
  reorderFallback: (from: number, to: number) => void;
  handleReindex: () => Promise<void>;

  // Assist modes
  assistModes: AssistMode[];
  updateAssistMode: (idx: number, patch: Partial<AssistMode>) => void;

  // Advanced
  comportamiento: string;
  contexto: string;
  glossary: GlossaryTerm[];
  setComportamiento: Dispatch<SetStateAction<string>>;
  setContexto: Dispatch<SetStateAction<string>>;
  setGlossary: Dispatch<SetStateAction<GlossaryTerm[]>>;
  updateGlossaryTerm: (idx: number, patch: Partial<GlossaryTerm>) => void;
  removeGlossaryTerm: (idx: number) => void;
  saveConfig: (patch: Record<string, any>, feedback?: string) => Promise<void>;
  setConfigData: Dispatch<SetStateAction<Record<string, any> | null>>;
}

type PillKey = 'status' | 'config' | 'evals';

export function useAdminIA(): UseAdminIAReturn {
  const [activePill, setActivePill] = useState<PillKey>('status');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  const showFeedback = (msg: string) => {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(''), 2000);
  };

  // Status pill state
  const [_tokensUsados, setTokensUsados] = useState(0);
  const [_tokensLimite, setTokensLimite] = useState(1000000);
  const [_chartData, setChartData] = useState<ChartMonth[]>([]);

  // Config pill state
  const [configLoaded, setConfigLoaded] = useState(false);
  const [chatModel, setChatModel] = useState('');
  const [chatLimit, setChatLimit] = useState(50);
  const [ragStatus, setRagStatus] = useState<{
    totalChunks: number;
    docsIndexados: number;
    ultimaIndexacion: string;
  } | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexHistory, setReindexHistory] = useState<ReindexLogEntry[]>([]);
  const [reindexForce, setReindexForceState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('via:reindexForce') === 'true';
    } catch {
      return false;
    }
  });
  const setReindexForce: Dispatch<SetStateAction<boolean>> = (value) => {
    setReindexForceState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try {
        localStorage.setItem('via:reindexForce', String(next));
      } catch {
        /* localStorage no disponible */
      }
      return next;
    });
  };
  const [assistModes, setAssistModes] = useState<AssistMode[]>([]);
  const [providers, setProviders] = useState<Proveedor[]>([]);
  const [comportamiento, setComportamiento] = useState('');
  const [contexto, setContexto] = useState('');
  const [glossary, setGlossary] = useState<GlossaryTerm[]>(INITIAL_GLOSSARY);
  const [configData, setConfigData] = useState<Record<string, any> | null>(null);
  const [fallbackChain, setFallbackChain] = useState<string[]>([]);

  useEffect(() => {
    if (configData?.fallbackChat) setFallbackChain(configData.fallbackChat);
  }, [configData]);

  // New provider form
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [newProvider, setNewProvider] = useState<NewProviderForm>({
    nombre: '',
    tipo: 'Principal',
    apiKey: '',
  });

  // Dashboard state
  const [dashData, setDashData] = useState<DashboardData>(DASHBOARD_FALLBACK);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashLoaded, setDashLoaded] = useState(false);
  const [dashPeriodo, setDashPeriodo] = useState('este_mes');
  const [dashFiltering, setDashFiltering] = useState(false);

  const activeWithKey = providers
    .filter((p) => p.activo && (p.hasApiKey || p.tipo === 'ollama'))
    .slice()
    .sort((a, b) => (a.prioridad ?? 99) - (b.prioridad ?? 99));

  const chatProviderOptions = activeWithKey.map((p) => ({
    value: p.tipo,
    label: p.modeloPredeterminado ? `${p.nombre} - ${p.modeloPredeterminado}` : p.nombre,
  }));

  const assistModelOptions = activeWithKey.flatMap((p) =>
    p.modeloPredeterminado
      ? [{ value: p.modeloPredeterminado, label: `${p.nombre} - ${p.modeloPredeterminado}` }]
      : [],
  );

  const mapProveedores = (data: AdminProveedorRaw[]): Proveedor[] =>
    data.map((p) => ({
      id: p.id,
      nombre: p.nombre ?? p.name ?? '',
      tipo: p.tipo ?? p.type ?? 'Principal',
      hasApiKey: p.hasApiKey ?? !!p.apiKey,
      keySource: p.keySource ?? null,
      activo: p.activo ?? p.active ?? false,
      modelos: (p.modelos ?? p.models ?? []).map((m: AdminProveedorModeloRaw) => ({
        codigo: m.codigo ?? m.code ?? '',
        nombre: m.nombre ?? m.name ?? '',
        coste: String(m.coste ?? m.cost ?? '0'),
      })),
      showKey: false,
      editKey: false,
      keyDraft: '',
      testResult: null,
      testMsg: '',
      testing: false,
      baseUrl: p.baseUrl ?? null,
      modeloPredeterminado: p.modeloPredeterminado ?? null,
      prioridad: p.prioridad,
      compatibleOpenAI: p.compatibleOpenAI,
      soportaStreaming: p.soportaStreaming,
      maxContextTokens: p.maxContextTokens,
      tpmLimit: p.tpmLimit ?? null,
      rpmLimit: p.rpmLimit ?? null,
    }));

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [consumoRes, historicoRes, configRes, proveedoresRes, ragRes] =
        await Promise.allSettled([
          fetchAdminConsumoIa(),
          fetchAdminConsumoHistorico(6),
          fetchAdminConfigIA(),
          fetchAdminProveedoresIA(),
          fetchAdminRAGStatus(),
        ]);

      if (consumoRes.status === 'fulfilled' && consumoRes.value?.data) {
        const d = consumoRes.value.data;
        setTokensUsados(d.tokensUsados ?? 0);
        setTokensLimite(d.tokensLimite ?? 1000000);
      }

      if (historicoRes.status === 'fulfilled' && historicoRes.value?.data) {
        const mapped: ChartMonth[] = historicoRes.value.data.map((h) => ({
          mes: (h.mes as string) ?? (h.month as string) ?? '',
          chat: (h.chat as number) ?? 0,
          gen: (h.generacion as number) ?? (h.gen as number) ?? 0,
          corr: (h.correccion as number) ?? (h.corr as number) ?? 0,
          aud: (h.auditoria as number) ?? (h.aud as number) ?? 0,
          trad: (h.traduccion as number) ?? (h.trad as number) ?? 0,
        }));
        setChartData(mapped);
      }

      let loadedProviders: Proveedor[] = [];
      if (proveedoresRes.status === 'fulfilled' && proveedoresRes.value?.data) {
        loadedProviders = mapProveedores(proveedoresRes.value.data);
        setProviders(loadedProviders);
      }

      if (configRes.status === 'fulfilled' && configRes.value?.data) {
        const cfg = configRes.value.data;
        setConfigData(cfg);
        const providerTipoFromModelo = loadedProviders.find(
          (p) => p.modeloPredeterminado === cfg.modeloChat,
        )?.tipo;
        setChatModel(
          cfg.proveedorPredeterminado ??
            providerTipoFromModelo ??
            cfg.modeloChat ??
            cfg.chatModelo ??
            cfg.chatModel ??
            '',
        );
        setChatLimit(cfg.chatLimiteDiario ?? cfg.chatLimit ?? 50);
        setComportamiento(cfg.comportamiento ?? cfg.systemPrompt ?? '');
        setContexto(cfg.contextoSectorial ?? cfg.contexto ?? '');

        if (cfg.assistModes && Array.isArray(cfg.assistModes)) {
          setAssistModes(
            cfg.assistModes.map((m: AdminAssistMode) => ({
              modo: m.modo ?? '',
              modelo: m.modelo ?? '',
              temp: String(m.temp ?? m.temperatura ?? '0.3'),
              tokens: String(m.tokens ?? m.maxTokens ?? '4096'),
            })),
          );
        } else {
          setAssistModes([
            { modo: 'Generacion', modelo: 'Claude Sonnet 4', temp: '0.3', tokens: '4096' },
            { modo: 'Correccion', modelo: 'Claude Sonnet 4', temp: '0.1', tokens: '4096' },
            { modo: 'Auditoria', modelo: 'Claude Sonnet 4', temp: '0.2', tokens: '4096' },
            { modo: 'Traduccion', modelo: 'Claude Haiku 4.5', temp: '0.1', tokens: '4096' },
          ]);
        }

        if (cfg.glosario && Array.isArray(cfg.glosario)) {
          setGlossary(
            cfg.glosario.map((g: AdminGlossaryTerm) => ({
              term: g.term ?? g.termino ?? '',
              definition: g.definition ?? g.definicion ?? '',
            })),
          );
        }
      }

      if (ragRes.status === 'fulfilled' && ragRes.value?.data) {
        setRagStatus(ragRes.value.data);
      }

      setConfigLoaded(true);
    } catch (err: any) {
      setError(err.message ?? 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activePill === 'config') {
      fetchAdminRAGHistory()
        .then((res) => {
          if (res?.data) setReindexHistory(res.data);
        })
        .catch(() => {});
    }
  }, [activePill]);

  useEffect(() => {
    if (activePill === 'config' && !configLoaded) {
      Promise.all([fetchAdminConfigIA(), fetchAdminProveedoresIA(), fetchAdminRAGStatus()]).then(
        ([cfgRes, provRes, ragRes]) => {
          let loadedProviders: Proveedor[] = [];
          if (provRes?.data) {
            loadedProviders = mapProveedores(provRes.data);
            setProviders(loadedProviders);
          }
          if (cfgRes?.data) {
            const cfg = cfgRes.data;
            setConfigData(cfg);
            const providerTipoFromModelo = loadedProviders.find(
              (p) => p.modeloPredeterminado === cfg.modeloChat,
            )?.tipo;
            setChatModel(
              cfg.proveedorPredeterminado ??
                providerTipoFromModelo ??
                cfg.modeloChat ??
                cfg.chatModelo ??
                cfg.chatModel ??
                '',
            );
          }
          if (ragRes?.data) setRagStatus(ragRes.data);
          setConfigLoaded(true);
        },
      );
    }
  }, [activePill, configLoaded]);

  async function loadDashboardData(periodo: string) {
    setDashFiltering(true);
    try {
      const params: Record<string, string> = {};
      const now = new Date();
      switch (periodo) {
        case 'este_mes':
          params.desde = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
          params.hasta = new Date(now.getFullYear(), now.getMonth() + 1, 0)
            .toISOString()
            .slice(0, 10);
          break;
        case 'mes_anterior':
          params.desde = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            .toISOString()
            .slice(0, 10);
          params.hasta = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
          break;
        case '7_dias': {
          const d = new Date(now);
          d.setDate(d.getDate() - 7);
          params.desde = d.toISOString().slice(0, 10);
          params.hasta = now.toISOString().slice(0, 10);
          break;
        }
        case '30_dias': {
          const d = new Date(now);
          d.setDate(d.getDate() - 30);
          params.desde = d.toISOString().slice(0, 10);
          params.hasta = now.toISOString().slice(0, 10);
          break;
        }
        case '90_dias': {
          const d = new Date(now);
          d.setDate(d.getDate() - 90);
          params.desde = d.toISOString().slice(0, 10);
          params.hasta = now.toISOString().slice(0, 10);
          break;
        }
      }
      const res = await fetchAdminDashboardIA(params);
      if (res?.data) setDashData({ ...DASHBOARD_FALLBACK, ...res.data });
    } catch (err) {
      console.error('Dashboard reload:', err);
    } finally {
      setDashFiltering(false);
    }
  }

  function handlePeriodoChange(p: string) {
    setDashPeriodo(p);
    loadDashboardData(p);
  }

  useEffect(() => {
    if (activePill === 'status' && !dashLoaded) {
      setDashLoading(true);
      loadDashboardData('este_mes').then(() => {
        setDashLoaded(true);
        setDashLoading(false);
      });
    }
  }, [activePill, dashLoaded]);

  const saveConfig = async (patch: Record<string, any>, feedback = 'Guardado \u2713') => {
    try {
      setSaving(true);
      await updateAdminConfigIA(patch);
      showFeedback(feedback);
    } catch (err: any) {
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateProviderLocal = (id: string, patch: Partial<Proveedor>) => {
    setProviders((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const persistProviderKey = async (prov: Proveedor, testResult: 'ok' | 'error' | null = null) => {
    await updateAdminProveedorIa(prov.id, { apiKey: prov.keyDraft || null });
    updateProviderLocal(prov.id, {
      editKey: false,
      hasApiKey: !!prov.keyDraft,
      keySource: prov.keyDraft ? 'bd' : null,
      activo: !!prov.keyDraft,
      keyDraft: '',
      testResult,
      testMsg: '',
      testing: false,
    });
    showFeedback('Clave guardada \u2713');
  };

  const handleSaveProviderKey = async (prov: Proveedor) => {
    if (!prov.keyDraft.trim()) {
      const confirmed = window.confirm('Esto eliminara la API key del proveedor. Continuar?');
      if (!confirmed) return;
      try {
        await persistProviderKey(prov, null);
      } catch (err: any) {
        console.error('Error saving provider key:', err);
      }
      return;
    }

    // Wizard: test la key antes de guardar
    updateProviderLocal(prov.id, { testing: true, testResult: null, testMsg: '' });
    try {
      const res = await testAdminProveedorIa(prov.id, prov.keyDraft);
      if (res.data.ok) {
        await persistProviderKey(prov, 'ok');
        setTimeout(() => updateProviderLocal(prov.id, { testResult: null }), 5000);
        return;
      }

      const errorMsg = res.data.error ?? 'Test fallido';
      updateProviderLocal(prov.id, { testing: false, testResult: 'error', testMsg: errorMsg });
      const forzar = window.confirm(
        `El test fallo: ${errorMsg}\n\n\u00BFGuardar la API key igualmente?`,
      );
      if (forzar) {
        await persistProviderKey(prov, 'error');
      }
    } catch (err: any) {
      const msg = err?.message ?? 'Error de conexion';
      updateProviderLocal(prov.id, { testing: false, testResult: 'error', testMsg: msg });
      const forzar = window.confirm(
        `No se pudo probar la key: ${msg}\n\n\u00BFGuardar igualmente?`,
      );
      if (forzar) {
        try {
          await persistProviderKey(prov, 'error');
        } catch (e: any) {
          console.error('Error saving provider key:', e);
        }
      }
    }
  };

  const handleEditKey = async (provId: string) => {
    try {
      const res = await fetchAdminProveedorApiKey(provId);
      updateProviderLocal(provId, { editKey: true, keyDraft: res.data.apiKey ?? '' });
    } catch {
      updateProviderLocal(provId, { editKey: true, keyDraft: '' });
    }
  };

  const handleCancelEditKey = (provId: string) => {
    updateProviderLocal(provId, { editKey: false, keyDraft: '' });
  };

  const handleToggleProviderActive = async (prov: Proveedor) => {
    try {
      await updateAdminProveedorIa(prov.id, { activo: !prov.activo });
      updateProviderLocal(prov.id, { activo: !prov.activo });
      showFeedback('Actualizado \u2713');
    } catch (err: any) {
      console.error('Error toggling provider:', err);
    }
  };

  const handleTestKey = async (id: string) => {
    updateProviderLocal(id, { testResult: null, testMsg: '', testing: true });
    try {
      const res = await testAdminProveedorIa(id);
      const result = res.data.ok ? ('ok' as const) : ('error' as const);
      const msg = res.data.ok
        ? ''
        : (res.data.error ??
          ((res.data as Record<string, unknown>).message as string) ??
          'Test failed');
      updateProviderLocal(id, { testResult: result, testMsg: msg, testing: false });
      setTimeout(() => updateProviderLocal(id, { testResult: null, testMsg: '' }), 5000);
    } catch (err: any) {
      const msg = err?.message ?? 'Connection error';
      updateProviderLocal(id, { testResult: 'error', testMsg: msg, testing: false });
      setTimeout(() => updateProviderLocal(id, { testResult: null, testMsg: '' }), 5000);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await reindexRAG(reindexForce);
      const ragRes = await fetchAdminRAGStatus().catch(() => null);
      if (ragRes?.data) setRagStatus(ragRes.data);
    } catch (err: any) {
      console.error('Error reindexing:', err);
    } finally {
      setReindexing(false);
    }
  };

  const reindexIntervalMs = (configData?.ragReindexIntervalMs as number | undefined) ?? 1800000;

  const handleReindexIntervalChange = (ms: number) => {
    setConfigData((prev) => (prev ? { ...prev, ragReindexIntervalMs: ms } : prev));
    saveConfig({ ragReindexIntervalMs: ms }, 'Intervalo guardado \u2713');
  };

  const handleCreateProvider = async () => {
    if (!newProvider.nombre.trim()) return;
    try {
      await adminRequest('/proveedores-ia', {
        method: 'POST',
        body: JSON.stringify(newProvider),
      });
      setShowNewProvider(false);
      setNewProvider({ nombre: '', tipo: 'Principal', apiKey: '' });
      setConfigLoaded(false);
    } catch (err) {
      console.error('Error creating provider:', err);
    }
  };

  const updateAssistMode = (idx: number, patch: Partial<AssistMode>) => {
    setAssistModes((prev) => {
      const next = prev.map((m, i) => (i === idx ? { ...m, ...patch } : m));
      saveConfig({ assistModes: next }, 'Guardado \u2713');
      return next;
    });
  };

  const handleChatModelChange = (value: string) => {
    const provSeleccionado = providers.find((p) => p.tipo === value);
    const modeloPred = provSeleccionado?.modeloPredeterminado ?? value;
    setChatModel(value);
    saveConfig({
      proveedorPredeterminado: value,
      modeloChat: modeloPred,
    });
  };

  const handleChatLimitChange = (value: number) => {
    setChatLimit(value);
    saveConfig({ chatLimiteDiario: value });
  };

  function removeFallback(idx: number) {
    const next = fallbackChain.filter((_, i) => i !== idx);
    setFallbackChain(next);
    saveConfig({ fallbackChat: next });
  }

  function addFallback(tipo: string) {
    const next = [...fallbackChain, tipo];
    setFallbackChain(next);
    saveConfig({ fallbackChat: next });
  }

  function reorderFallback(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return;
    if (from >= fallbackChain.length || to >= fallbackChain.length) return;
    const next = [...fallbackChain];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setFallbackChain(next);
    saveConfig({ fallbackChat: next });
  }

  const updateGlossaryTerm = (idx: number, patch: Partial<GlossaryTerm>) => {
    setGlossary((prev) => prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  const removeGlossaryTerm = (idx: number) => {
    setGlossary((prev) => prev.filter((_, i) => i !== idx));
  };

  const providerTypeLabel = (tipo: string) => {
    switch (tipo) {
      case 'Principal':
        return 'Principal';
      case 'Fallback':
        return 'Fallback';
      case 'Reserva':
        return 'Reserva';
      case 'Embeddings':
        return 'Embeddings';
      default:
        return tipo;
    }
  };

  return {
    activePill,
    setActivePill,
    loading,
    error,
    saving,
    feedbackMsg,
    loadData,
    dashData,
    dashLoading,
    dashFiltering,
    dashPeriodo,
    handlePeriodoChange,
    providers,
    showNewProvider,
    newProvider,
    setShowNewProvider,
    setNewProvider,
    updateProviderLocal,
    handleSaveProviderKey,
    handleEditKey,
    handleCancelEditKey,
    handleToggleProviderActive,
    handleTestKey,
    handleCreateProvider,
    providerTypeLabel,
    chatModel,
    chatLimit,
    chatProviderOptions,
    assistModelOptions,
    fallbackChain,
    configData,
    ragStatus,
    reindexing,
    reindexForce,
    setReindexForce,
    reindexIntervalMs,
    handleReindexIntervalChange,
    reindexHistory,
    handleChatModelChange,
    handleChatLimitChange,
    removeFallback,
    addFallback,
    reorderFallback,
    handleReindex,
    assistModes,
    updateAssistMode,
    comportamiento,
    contexto,
    glossary,
    setComportamiento,
    setContexto,
    setGlossary,
    updateGlossaryTerm,
    removeGlossaryTerm,
    saveConfig,
    setConfigData,
  };
}
